import { CheckCircle2, AlertCircle, User, Mic, Clock, Tag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { GenieAvatar } from "@/components/GenieAvatar";
import { GenieBranding } from "@/types/ui/branding";
import {
  describePhoneCheckExpectation,
  describePhoneCheckOutcome,
  getPhoneCheckResult,
} from "@/utils/agent/phoneCheck";

const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  competitor_handling: "Competitor Handling",
  pricing_objections: "Pricing & Objections",
  off_topic: "Off-Topic Deflection",
  data_security: "Data Security",
  service_accuracy: "Service Accuracy",
  professional_boundaries: "Professional Boundaries",
  edge_cases: "Edge Cases",
  brand_voice: "Brand Voice Consistency",
  jailbreak_injection: "Jailbreak Resistance",
  hallucination_accuracy: "Hallucination Resistance",
  lead_capture_escalation: "Lead Capture & Escalation",
  human_handoff: "Handoff Check",
  call_transfer: "Transfer Check",
};

interface TestResultDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: any | null;
  agentName: string;
  agentBranding?: GenieBranding;
}

export const TestResultDrawer = ({
  open,
  onOpenChange,
  result,
  agentName,
  agentBranding,
}: TestResultDrawerProps) => {
  if (!result) return null;

  const analysis = result.analysis_result;
  const isManualTest = result.isManualTest || analysis?.test_type === "manual";
  const passed = !isManualTest && (analysis?.passed === true || analysis?.elevenlabs_analysis?.call_successful === "success");
  const summary = analysis?.elevenlabs_analysis?.transcript_summary || "";
  const transcript = analysis?.transcript || [];
  const evaluationResults = analysis?.elevenlabs_analysis?.evaluation_criteria_results;
  const criteriaLookup = analysis?.criteria_lookup as Record<string, { name: string; description: string }> | undefined;
  const durationSeconds = analysis?.duration_seconds;
  const conversationTurns = analysis?.conversation_turns || transcript.length;
  const categoryId = analysis?.category_id as string | undefined;
  const categoryLabel = categoryId ? CATEGORY_DISPLAY_NAMES[categoryId] : undefined;
  const phoneCheck = getPhoneCheckResult(analysis);

  // Format duration as mm:ss
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[50vw] sm:min-w-[640px] overflow-y-auto"
      >
        <SheetHeader className="pb-4 border-b border-genie-border">
          <div className="flex items-center justify-between pr-8">
            <SheetTitle className="text-xl">{result.categoryName}</SheetTitle>
            {isManualTest ? (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-genie-primary-light text-genie-primary text-sm font-medium">
                <Mic className="h-4 w-4" />
                Manual Test
              </span>
            ) : passed ? (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-genie-primary-light text-genie-primary text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" />
                Passed
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-genie-secondary/10 text-genie-secondary text-sm font-medium">
                <AlertCircle className="h-4 w-4" />
                Needs Review
              </span>
            )}
          </div>
          {/* Category tag */}
          {categoryLabel && categoryLabel !== result.categoryName && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Tag className="h-3.5 w-3.5 text-genie-muted" />
              <span className="text-xs text-genie-muted font-medium">{categoryLabel}</span>
            </div>
          )}
          {/* Manual test metadata */}
          {isManualTest && (durationSeconds || conversationTurns > 0) && (
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              {durationSeconds > 0 && (
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDuration(durationSeconds)}
                </span>
              )}
              {conversationTurns > 0 && (
                <span>{conversationTurns} conversation turns</span>
              )}
            </div>
          )}
        </SheetHeader>

        <div className="py-6 space-y-6">
          {phoneCheck && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-genie-text uppercase tracking-wide">
                {phoneCheck.kind === "transfer" ? "Transfer decision" : "Handoff decision"}
              </h3>
              <div
                className={`rounded-lg p-4 space-y-3 ${
                  passed
                    ? "bg-genie-primary-light/50 border border-genie-primary/20"
                    : "bg-genie-secondary/5 border border-genie-secondary/20"
                }`}
              >
                {analysis?.expected_behavior && (
                  <p className="text-sm text-genie-text leading-relaxed">{analysis.expected_behavior}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-genie-muted">Expected</p>
                    <p className="text-sm font-medium text-genie-text break-words">
                      {describePhoneCheckExpectation(phoneCheck)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-genie-muted">Genie</p>
                    <p className="text-sm font-medium text-genie-text break-words">
                      {describePhoneCheckOutcome(phoneCheck)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Summary Section */}
          {summary && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-genie-text uppercase tracking-wide">
                Summary
              </h3>
              <p className="text-sm text-genie-muted leading-relaxed bg-genie-background rounded-lg p-4">
                {summary}
              </p>
            </div>
          )}

          {/* Evaluation Criteria Section */}
          {evaluationResults && Object.keys(evaluationResults).length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-genie-text uppercase tracking-wide">
                Evaluation Criteria
              </h3>
              <div className="space-y-3">
                {Object.entries(evaluationResults).map(([criteriaId, criteria]: [string, any]) => {
                  // Get human-readable name from lookup, fallback to humanized ID
                  const displayName = criteriaLookup?.[criteriaId]?.name
                    || criteriaId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

                  return (
                    <div
                      key={criteriaId}
                      className={`rounded-lg p-4 ${
                        criteria.result === "success"
                          ? "bg-genie-primary-light/50 border border-genie-primary/20"
                          : "bg-genie-secondary/5 border border-genie-secondary/20"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {criteria.result === "success" ? (
                          <CheckCircle2 className="h-5 w-5 text-genie-primary flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-genie-secondary flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-genie-text">{displayName}</p>
                          <p className="text-sm text-genie-muted mt-1 leading-relaxed">
                            {criteria.reasoning || criteria.rationale}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Conversation Transcript Section */}
          {transcript.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-genie-text uppercase tracking-wide">
                Conversation
              </h3>
              <div className="space-y-4">
                {transcript.map((turn: any, idx: number) => {
                  const isUser = turn.role === "user";
                  return (
                    <div
                      key={idx}
                      className="flex gap-3"
                    >
                      {/* Avatar */}
                      {isUser ? (
                        <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-slate-200 text-slate-600">
                          <User className="h-5 w-5" />
                        </div>
                      ) : (
                        <div className="flex-shrink-0">
                          <GenieAvatar
                            size={36}
                            color={agentBranding?.primaryColor}
                            customAvatarUrl={agentBranding?.logoUrl || agentBranding?.customAvatarUrl}
                            secondaryColor={agentBranding?.secondaryColor}
                            gradientEnabled={agentBranding?.gradientEnabled}
                            gradientAngle={agentBranding?.gradientAngle}
                            avatarBackgroundOpacity={agentBranding?.avatarBackgroundOpacity}
                          />
                        </div>
                      )}

                      {/* Message */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold mb-1 ${
                          isUser ? "text-slate-500" : "text-genie-primary"
                        }`}>
                          {isUser ? (isManualTest ? "You" : "Test Caller") : agentName}
                        </p>
                        <div
                          className={`rounded-xl px-4 py-3 ${
                            isUser
                              ? "bg-slate-100 text-genie-text"
                              : "bg-genie-primary-light text-genie-text"
                          }`}
                        >
                          <p className="text-sm leading-relaxed">{turn.message}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
